import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useChats } from '../store/chats.ts'
import { useAuth } from '../store/auth.ts'
import { api } from '../api/client.ts'
import { ProfileSettings } from './ProfileSettings.tsx'
import { FamilyPanel } from './FamilyPanel.tsx'
import { NotificationSettings } from './NotificationSettings.tsx'
import { readFeedSeen } from '../pages/FeedPage.tsx'
import { Icon, type IconName } from './Icon.tsx'
import { builtAt } from '../lib/build.ts'

/**
 * ПЕРЕХОД МЕЖДУ РАЗДЕЛАМИ
 *
 * На широком экране — узкая полоса слева со всеми разделами. На телефоне
 * внизу помещается четыре, поэтому там четыре главных и кнопка «ещё».
 *
 * Главные выбраны по тому, как часто в них заходят, а не по важности:
 * в переписку и список покупок заглядывают каждый день, в документы —
 * раз в полгода.
 *
 * Открытый сейчас раздел из «ещё» показывается внизу вместо последнего
 * главного: иначе, зайдя в документы, человек не видит, где находится.
 */

type Section = {
  to: string
  name: string
  icon: IconName
}

const SECTIONS: Section[] = [
  { to: '/', name: 'Чаты', icon: 'chats' },
  { to: '/feed', name: 'Что нового', icon: 'feed' },
  { to: '/shopping', name: 'Покупки', icon: 'shopping' },
  { to: '/planner', name: 'Планер', icon: 'planner' },
  { to: '/health', name: 'Здоровье', icon: 'health' },
  { to: '/recipes', name: 'Еда', icon: 'recipes' },
  { to: '/budget', name: 'Бюджет', icon: 'budget' },
  { to: '/wallet', name: 'Кошелёк', icon: 'wallet' },
  { to: '/documents', name: 'Документы', icon: 'documents' },
  { to: '/wishes', name: 'Желания', icon: 'wishes' },
  { to: '/movies', name: 'Кино', icon: 'movies' },
]

/** Сколько разделов помещается в строку внизу вместе с кнопкой «ещё». */
const ON_PHONE = 4

/** «19 сентября, 14:07» — коротко и по-человечески. */
function buildDate(): string {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(builtAt))
  } catch {
    return 'неизвестно'
  }
}

export function AppNav() {
  const unread = useChats((s) => s.chats.reduce((sum, c) => sum + (c.archived ? 0 : c.unread), 0))
  const location = useLocation()
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)

  const [more, setMore] = useState(false)
  const [openSheet, setOpenSheet] = useState<'profile' | 'family' | 'push' | null>(null)

  // Заходили ли в ленту после последнего изменения — узнаём у сервера
  // лениво: точный счётчик здесь не нужен, хватает «есть ли новое»
  const [hasNews, setHasNews] = useState(false)

  /**
   * Есть ли в ленте что-то после последнего захода.
   *
   * Спрашиваем одну последнюю запись, а не считаем непрочитанное:
   * «сколько новостей» никому не нужно, нужно «стоит ли заглядывать».
   */
  useEffect(() => {
    api<{ events: { createdAt: string }[] }>('/api/feed?limit=1')
      .then((data) => {
        const newest = data.events[0]
        setHasNews(newest ? new Date(newest.createdAt).getTime() > readFeedSeen() : false)
      })
      .catch(() => setHasNews(false))
  }, [location.pathname])

  const isHere = (section: Section): boolean =>
    section.to === '/'
      ? location.pathname === '/' || location.pathname.startsWith('/chat/')
      : location.pathname.startsWith(section.to)

  const primary = SECTIONS.slice(0, ON_PHONE)
  const openHidden = SECTIONS.slice(ON_PHONE).find(isHere)

  const onPhone = openHidden ? [...primary.slice(0, ON_PHONE - 1), openHidden] : primary
  const shownOnPhone = new Set(onPhone.map((s) => s.to))

  return (
    <>
      <nav className="app-nav">
        {SECTIONS.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            end={section.to === '/'}
            className={({ isActive }) =>
              'nav-item' +
              (shownOnPhone.has(section.to) ? '' : ' is-extra') +
              (isActive ? ' is-active' : '')
            }
          >
            <span className="nav-icon" aria-hidden="true">
              <Icon name={section.icon} />
              {section.to === '/' && unread > 0 && (
                <span className="nav-badge">{unread > 99 ? '99+' : unread}</span>
              )}
              {/* Точка на ленте: сколько там нового, считать незачем —
                  важно только, стоит ли туда заглядывать */}
              {section.to === '/feed' && hasNews && <span className="nav-dot" />}
            </span>
            <span className="nav-name">{section.name}</span>
          </NavLink>
        ))}

        {/* Кнопка видна только на телефоне: в полосе слева помещается всё */}
        <button className="nav-item nav-more" onClick={() => setMore(true)}>
          <span className="nav-icon" aria-hidden="true">
            <Icon name="more" />
          </span>
          <span className="nav-name">Ещё</span>
        </button>
      </nav>

      {more && (
        <div className="sheet" onClick={() => setMore(false)}>
          <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <h2>Разделы</h2>
              <button className="icon-btn" onClick={() => setMore(false)} aria-label="Закрыть">
                ✕
              </button>
            </div>

            <div className="section-grid">
              {SECTIONS.map((section) => (
                <NavLink
                  key={section.to}
                  to={section.to}
                  end={section.to === '/'}
                  className={({ isActive }) =>
                    'section-tile' + (isActive ? ' is-active' : '')
                  }
                  onClick={() => setMore(false)}
                >
                  <span className="section-tile-icon" aria-hidden="true">
                    <Icon name={section.icon} size={26} />
                  </span>
                  <span>{section.name}</span>
                </NavLink>
              ))}
            </div>

            {/* Настройки раньше открывались только из «Чатов»: пока
                разделов было два, это работало, а из «Документов» до них
                было уже не добраться */}
            <h3 className="sheet-sub">Настройки</h3>
            <div className="member-list">
              <button
                className="member-row pick"
                onClick={() => {
                  setOpenSheet('profile')
                  setMore(false)
                }}
              >
                <span className="circle-badge" aria-hidden="true">
                  <Icon name="settings" size={20} />
                </span>
                <div className="member-text">
                  <div className="member-name">{user?.displayName ?? 'Профиль'}</div>
                  <div className="member-sub">Имя, фото, день рождения</div>
                </div>
              </button>

              <button
                className="member-row pick"
                onClick={() => {
                  setOpenSheet('family')
                  setMore(false)
                }}
              >
                <span className="circle-badge" aria-hidden="true">
                  <Icon name="family" size={20} />
                </span>
                <div className="member-text">
                  <div className="member-name">Семья и приглашения</div>
                </div>
              </button>

              <button
                className="member-row pick"
                onClick={() => {
                  setOpenSheet('push')
                  setMore(false)
                }}
              >
                <span className="circle-badge" aria-hidden="true">
                  <Icon name="bell" size={20} />
                </span>
                <div className="member-text">
                  <div className="member-name">Уведомления</div>
                </div>
              </button>

              {user?.isAdmin && (
                <Link to="/admin" className="member-row pick" onClick={() => setMore(false)}>
                  <span className="circle-badge" aria-hidden="true">
                    <Icon name="admin" size={20} />
                  </span>
                  <div className="member-text">
                    <div className="member-name">Состояние сервера</div>
                  </div>
                </Link>
              )}

              <button className="member-row pick" onClick={logout}>
                <span className="circle-badge" aria-hidden="true">
                  <Icon name="exit" size={20} />
                </span>
                <div className="member-text">
                  <div className="member-name">Выйти</div>
                </div>
              </button>

              {/**
               * Когда собрана версия, которая сейчас работает.
               *
               * Установленное приложение может держать в кеше вчерашнюю
               * сборку сутками: человек видит старое и считает, что
               * починки не случилось. Теперь это можно просто прочитать,
               * а не гадать.
               */}
              <div className="build-stamp" title="Версия приложения">
                Версия от {buildDate()}
              </div>
            </div>
          </div>
        </div>
      )}

      {openSheet === 'profile' && <ProfileSettings onClose={() => setOpenSheet(null)} />}
      {openSheet === 'family' && <FamilyPanel onClose={() => setOpenSheet(null)} />}
      {openSheet === 'push' && <NotificationSettings onClose={() => setOpenSheet(null)} />}
    </>
  )
}
